import { T, useTranslate } from '@tolgee/react';
import { Drawer, Tooltip } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import QRCode from 'react-qr-code';
import styled, { css } from 'styled-components';

import { CheckSvg, CopySvg } from '../../../assets/icons';
import ProfileSvg from '../../../assets/icons/ProfileSvg';
import theme from '../../../assets/theme';
import useGenerateNewGamePin from '../../../hooks/useGenerateNewGamePin';
import { useMetrics } from '../../../hooks/useMetrics';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { Game, PartialParticipant } from '../../../services/backendService/types';
import { getConfig } from '../../../utils/store';
import { zIndexes } from '../../../utils/zIndexes';
import { LoadingDots } from '../../_atoms/DismissableQuestionList/DismissableQuestionList';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { CenteredContainer } from '../../styled/CenteredContainer';
import BaseModal from '../BaseModal';
import MaxSizeMediaContent from '../MaxSizeMediaContent';
import { MaxSizeMediaContentContext } from '../MaxSizeMediaContent/MaxSizeMediaContent';
const animationProps = {
  animate: {
    scale: [0, 1.05, 1],
  },
  transition: {
    duration: 0.6,
    type: 'spring',
  },
};
const LobbyDropdown = ({
  game,
  setShowLobby,
  showLobby,
  participants,
  participantsCount,
}: {
  game: Game;
  tenantId: string;
  showLobby: boolean;
  setShowLobby: (show: boolean) => void;
  participants: PartialParticipant[];
  participantsCount: number;
}) => {
  const metrics = useMetrics();
  const [renderedParticipants, setRenderedParticipants] = useState<PartialParticipant[]>(
    [],
  );
  const { t } = useTranslate();
  const url = getConfig().game.url;
  const [gameUrlCopied, setGameUrlCopied] = useState(false);
  const [pinCopied, setPinCopied] = useState(false);
  const { pin: newPin } = useGenerateNewGamePin(game.id, game.pin);
  const pinToUse = game.pin || newPin || '-';
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [windowWidth] = useWindowSize();

  useEffect(() => {
    const ref = setTimeout(() => {
      setPinCopied(false);
      setGameUrlCopied(false);
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [pinCopied, gameUrlCopied]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedRenderParticipants = useCallback(
    debounce((participants: PartialParticipant[]) => {
      setRenderedParticipants(participants);
    }, 500),
    [],
  );

  // In preview mode with requireRealNames, filter out participants without names set
  // If names are not required, show all participants (they've completed join by entering pin)
  const filteredParticipants = useMemo(() => {
    if (game.isPreviewGame && game.requireRealNames) {
      return participants.filter(p => p.name !== undefined && p.name !== '');
    }
    return participants;
  }, [participants, game.isPreviewGame, game.requireRealNames]);

  useEffect(() => {
    if (filteredParticipants.length > 20) {
      debouncedRenderParticipants(filteredParticipants);
    } else {
      setRenderedParticipants(filteredParticipants);
    }
    // Cleanup function
    return () => {
      debouncedRenderParticipants.cancel();
    };
  }, [filteredParticipants, debouncedRenderParticipants]);

  return (
    <>
      <BaseModal
        visible={showQRCodeModal}
        width={'100vw'}
        hideCancel={true}
        onCancel={() => setShowQRCodeModal(false)}
        transparentBackground={true}
        maskStyle={{ backgroundColor: 'rgba(40,47,51,0.95)' }}
        style={{
          left: 0,
          top: 10,
          width: '100%',
        }}
      >
        <CenteredContainer style={{ width: '100%', height: '90vh' }}>
          <MaxSizeMediaContent aspectRatio={9 / 9}>
            <QRCodeContainer link={`https://${url}/join/${pinToUse}`} />
          </MaxSizeMediaContent>
        </CenteredContainer>
      </BaseModal>
      <ExpandingJoinContainer>
        <Drawer
          placement="top"
          headerStyle={{ display: 'none' }}
          closable={false}
          onClose={() => {
            metrics.logEvent('LiveGame.Lobby.Close', { byOnClose: true });
            if (participantsCount === 0) {
              return;
            }
            setShowLobby(false);
          }}
          visible={showLobby}
          getContainer={false}
          mask={true}
          maskStyle={{ backgroundColor: 'transparent' }}
        >
          <LobbyContainer>
            <JoinContainer>
              <UrlAndPinContainer>
                <CopyToClipboard
                  text={`https://${url}/join/${pinToUse}`}
                  onCopy={() => {
                    setGameUrlCopied(true);
                    setPinCopied(false);
                  }}
                >
                  <Tooltip
                    title={t(
                      'Join url copied to clipboard',
                      'Join url copied to clipboard',
                    )}
                    visible={gameUrlCopied}
                    placement="right"
                  >
                    <CopyTextSpan>
                      <T keyName="Join at" defaultValue="Join at" />
                      &nbsp;
                      <span style={{ fontWeight: 800 }}>{url.toLowerCase()} </span>
                      <CopyIconWrapper>
                        {!gameUrlCopied && <CopySvg />}
                        {gameUrlCopied && <CheckSvg />}
                      </CopyIconWrapper>
                    </CopyTextSpan>
                  </Tooltip>
                </CopyToClipboard>

                <CopyToClipboard
                  text={pinToUse}
                  onCopy={() => {
                    setPinCopied(true);
                    setGameUrlCopied(false);
                  }}
                >
                  <Tooltip
                    title={t('Pin copied to clipboard', 'Pin copied to clipboard')}
                    visible={pinCopied}
                    placement="right"
                  >
                    <CopyTextSpan>
                      <T keyName="with pin" defaultValue="with pin" />
                      :&nbsp;
                      <CopyTextSpanPin style={{ fontWeight: 800, letterSpacing: '2px' }}>
                        {pinToUse.substring(0, 3)}{' '}
                        {pinToUse.substring(3, pinToUse.length)}
                      </CopyTextSpanPin>
                      <CopyIconWrapper>
                        {!pinCopied && <CopySvg />}
                        {pinCopied && <CheckSvg />}
                      </CopyIconWrapper>
                    </CopyTextSpan>
                  </Tooltip>
                </CopyToClipboard>
              </UrlAndPinContainer>

              <Tooltip title={t('Click to expand QR code to fullscreen')}>
                <MaxSizeMediaContent
                  aspectRatio={9 / 9}
                  style={{
                    maxWidth: 800,
                    margin: '20px auto 0px auto',
                    maxHeight: windowWidth > 800 ? '75%' : '90%',
                  }}
                >
                  <QRCodeContainer
                    link={`https://${url}/join/${pinToUse}`}
                    onClick={() => setShowQRCodeModal(true)}
                  />
                </MaxSizeMediaContent>
              </Tooltip>
            </JoinContainer>

            <ParticipantsContainerWrapper>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  height: '75px',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{ marginRight: '10px', marginLeft: '10px', fontSize: '3vw' }}
                >
                  {game.isPreviewGame ? filteredParticipants.length : participantsCount}
                </span>
                <IconWrapper
                  height="40px"
                  width="40px"
                  onClick={() => {
                    setShowLobby(false);
                  }}
                >
                  <ProfileSvg />
                </IconWrapper>
              </div>
              <ParticipantsContainer>
                {(game.isPreviewGame
                  ? filteredParticipants.length
                  : participantsCount) === 0 && (
                  <span style={{ fontSize: '3vw' }}>
                    <T
                      keyName="Waiting for students to join"
                      defaultValue="Waiting for students to join"
                    />
                    <LoadingDots>...</LoadingDots>
                  </span>
                )}
                {renderedParticipants.length < 50 && (
                  <AnimatePresence>
                    {renderedParticipants.map(p => (
                      <motion.div key={p.id} {...animationProps}>
                        <ParticipantLabel
                          key={p.id}
                          $participantNumber={renderedParticipants.length || 1}
                        >
                          <span translate="no">{p.nickName}</span>
                        </ParticipantLabel>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
                {renderedParticipants.length >= 50 && (
                  <>
                    {renderedParticipants.map(p => (
                      <ParticipantLabel
                        key={p.id}
                        $participantNumber={renderedParticipants.length || 1}
                      >
                        <span translate="no">{p.nickName}</span>
                      </ParticipantLabel>
                    ))}
                  </>
                )}
              </ParticipantsContainer>
            </ParticipantsContainerWrapper>
          </LobbyContainer>
        </Drawer>
      </ExpandingJoinContainer>
    </>
  );
};

const QRCodeContainer = ({ onClick, link }: { onClick?: () => void; link: string }) => {
  const { height, width } = useContext(MaxSizeMediaContentContext);
  const QRCodeSize =
    useMemo(() => Math.min(height || 0, width || 0), [height, width]) * 0.9;
  return (
    <QRCodeWrapper
      onClick={() => onClick && onClick()}
      $isClickable={!!onClick}
      style={{ height: height, width: width }}
    >
      <QRCode value={link} size={QRCodeSize} />
    </QRCodeWrapper>
  );
};
const ExpandingJoinContainer = styled.div`
  .ant-drawer {
    z-index: ${zIndexes('PRESENTER_LOBBY_JOIN_CONTAINER')} !important;
  }

  .ant-drawer-body {
    padding: 0px;
  }

  .ant-drawer-content-wrapper {
    height: auto !important;
  }

  .ant-drawer.ant-drawer-open {
    z-index: ${zIndexes('PRESENTER_LOBBY_JOIN_CONTAINER')} !important;
    background: ${theme.colors.background};
    height: fit-content;
  }

  .ant-drawer.ant-drawer-open .ant-drawer-mask {
    background-color: none;
    height: 100vh;
  }
`;
const LobbyContainer = styled.div`
  display: grid;
  grid-template-columns: 40% 60%;
  background-color: ${theme.colors.background};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  height: 100vh;
  overflow: hidden;

  position: relative;
  z-index: ${zIndexes('PRESENTER_LOBBY')};

  button {
    text-decoration: underline;
    margin-bottom: 20px;
  }
`;

const JoinContainer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 100vh;
  background-color: ${theme.colors.green};
  padding: 20px;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  padding-bottom: 100px;

  @media (max-width: 800px) {
    flex-direction: column;
    display: grid;
    grid-auto-flow: row;
    grid-template-rows: 1fr 3fr;
    grid-column: 1/3;
    padding: 20px;
  }
`;

const CopyTextSpan = styled.span`
  cursor: pointer;
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  font-size: 2.5vw;
  @media (max-width: 800px) {
    font-size: 4vw;
  }
`;
const CopyTextSpanPin = styled(CopyTextSpan)`
  font-size: 4vw;
  @media (max-width: 800px) {
    font-size: 5vw;
  }
`;
const UrlAndPinContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  max-height: 25%;

  color: ${theme.colors.white};
  @media (max-width: 800px) {
    width: 95vw;
    max-height: unset;
  }
`;
const CopyIconWrapper = styled(IconWrapper)`
  margin-left: 5px;
  height: 100%;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const QRCodeWrapper = styled.div<{ $isClickable: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  transition: box-shadow 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  ${({ $isClickable }) =>
    $isClickable
      ? css`
          cursor: pointer;
        `
      : css`
          cursor: default;
        `}

  &:hover {
    ${({ $isClickable }) =>
      $isClickable &&
      css`
        filter: brightness(70%);
      `}
  }

  svg {
    background-color: ${theme.colors.white};
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
    border-radius: 20px;
    padding: 15px;
    box-sizing: content-box;

    @media (max-width: 800px) {
      padding: 5px;
    }

    &:hover {
      box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
    }
  }
`;

const ParticipantsContainerWrapper = styled.div`
  padding: 20px;
  @media (max-width: 800px) {
    grid-column: 1/3;
  }
`;

const ParticipantsContainer = styled.div`
  display: flex;
  align-content: flex-start;
  flex-wrap: wrap;
  overflow: scroll;
  height: 80vh;
  & > * {
    margin: 5px;
  }
  &:-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const ParticipantLabel = styled.div<{ $participantNumber: number }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.blue};
  padding: ${({ $participantNumber }) =>
    `${Math.exp(-0.1 * $participantNumber) + 5}px ${10 *
      Math.exp(-0.1 * $participantNumber) +
      5}px `};
  font-weight: bold;
  font-size: ${({ $participantNumber }) =>
    `${25 * Math.exp(-0.01 * $participantNumber) + 10}px `};
  border-radius: 10px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
`;

export default LobbyDropdown;
